import { getPolicyTypes, IPolicyType } from "@/api/policy-types";
import { SearchField } from "@/components/admin";
import { Table } from "@/components/shared/table/Table";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { formatDate } from "@/utils";
import { Chip } from "@mui/material";
import { GridColDef, GridSortModel } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { DeletePolicyTypeButton } from "./DeletePolicyTypeButton";
import { PolicyTypeDetailsButton } from "./PolicyTypeDetailsButton";

const columns: GridColDef<IPolicyType>[] = [
    {
        field: "id",
        headerName: "ID",
        display: "flex",
        width: 80,
    },
    {
        field: "name",
        headerName: "Name",
        display: "flex",
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }) => <PolicyTypeDetailsButton policyType={row} />,
    },
    {
        field: "short_name",
        headerName: "Short Name",
        display: "flex",
        width: 120,
    },
    {
        field: "skill",
        headerName: "Skill",
        display: "flex",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) =>
            row.skill ? (
                <Chip
                    label={row.skill.name.replace("Policy Type: ", "")}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ maxWidth: 100 }}
                />
            ) : (
                <span style={{ color: "#9e9e9e", fontSize: "0.8rem" }}>—</span>
            ),
    },
    {
        field: "updated_at",
        headerName: "Last Updated",
        type: "date",
        display: "flex",
        width: 150,
        valueFormatter: (value) => formatDate(value),
    },
    {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        getActions: ({ row }) => [
            <DeletePolicyTypeButton policyTypeId={row.id} />,
        ],
    },
];

export const PolicyTypesTable = () => {
    const {
        search,
        debouncedSearch,
        setSearch,
        clear: handleSearchClear,
    } = useDebouncedSearch();

    const [rows, setRows] = useState<IPolicyType[]>([]);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: "name", sort: "asc" },
    ]);

    useEffect(() => {
        setPaginationModel((prev) =>
            prev.page === 0 ? prev : { ...prev, page: 0 }
        );
    }, [debouncedSearch, sortModel]);

    const sortFilter =
        sortModel.length > 0
            ? { sort_by: sortModel[0].field, order: sortModel[0].sort ?? "asc" }
            : {};

    const { data, isFetching } = useQuery({
        queryKey: [
            "admin-policy-types",
            paginationModel.page,
            paginationModel.pageSize,
            debouncedSearch,
            sortModel,
        ],
        queryFn: () =>
            getPolicyTypes(
                paginationModel.page + 1,
                paginationModel.pageSize,
                sortFilter,
                debouncedSearch || undefined
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data?.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    return (
        <>
            <div className="mb-4">
                <SearchField
                    value={search}
                    onChange={setSearch}
                    onClear={handleSearchClear}
                    placeholder="Search policy types..."
                />
            </div>
            <Table
                loading={isFetching}
                columns={columns}
                rows={rows}
                disableColumnFilter
                pageSizeOptions={[10, 25, 50]}
                paginationModel={paginationModel}
                rowCount={rowCount}
                onPaginationModelChange={setPaginationModel}
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={setSortModel}
            />
        </>
    );
};
